/* Cart Drive - free-shipping progress bar + cart message/timer.
 *
 * Renders inside the Slide Cart app drawer (injected, same pattern as
 * checkout-plus.js) and on the theme cart page. The bar shows progress
 * toward the free-shipping threshold; the reminder row carries the
 * limited-run message and its ten-minute clock.
 *
 * The in-cart recommendation module that used to live here was removed on
 * 2026-09-17 (one attributed order in six weeks; see
 * docs/cart-cro-plan-2026-09-17.md). The drawer no longer fires product,
 * recommendation, or collection fetches on cart changes.
 *
 * Pure functions live under CartDrive._test for the e2e boundary checks.
 */
(function () {
  'use strict';

  var cfgEl = document.querySelector('script[data-cart-drive-config]');
  if (!cfgEl) return;

  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }

  var CP_HANDLE = 'checkout-plus';
  var THRESHOLD_CENTS = Math.round((cfg.threshold || 150) * 100);
  var RED_ZONE_CENTS = 4500; // inside the last ~$45 (about one tee)

  /* ------------------------------------------------------------------ */
  /* Shared state + utilities                                            */
  /* ------------------------------------------------------------------ */

  var state = {
    cart: null,
    schoolMap: null // slug -> {name, color}
  };

  var moneyFmt;
  try {
    moneyFmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: cfg.currency || 'USD' });
  } catch (e) {
    moneyFmt = null;
  }

  function formatMoney(cents) {
    if (moneyFmt) return moneyFmt.format(cents / 100);
    return '$' + (cents / 100).toFixed(2);
  }

  function formatMoneyShort(cents) {
    var s = formatMoney(cents);
    return s.replace(/\.00$/, '');
  }

  var origFetch = window.fetch.bind(window);

  function getJson(url) {
    return origFetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('fetch failed: ' + url);
      return r.json();
    });
  }

  function fetchCart() {
    return getJson('/cart.js').catch(function () { return null; });
  }

  function schoolMap() {
    if (state.schoolMap) return state.schoolMap;
    var el = document.querySelector('script[data-slidecart-schools]');
    if (!el) return {};
    try {
      state.schoolMap = JSON.parse(el.textContent);
    } catch (e) {
      state.schoolMap = {};
    }
    return state.schoolMap;
  }

  /* Longest-slug-first list, computed once, for handle matching. */
  var slugsByLength = null;
  function schoolSlugs() {
    if (!slugsByLength) {
      slugsByLength = Object.keys(schoolMap()).sort(function (a, b) { return b.length - a.length; });
    }
    return slugsByLength;
  }

  /* Same detection algorithm as the school badges snippet: tags first,
     then slug-in-handle. Returns a slug or null. */
  function detectSchool(tags, handle) {
    var map = schoolMap();
    var i;
    if (tags) {
      for (i = 0; i < tags.length; i++) {
        var t = String(tags[i]).toLowerCase().replace(/\s+/g, '-');
        if (map[t]) return t;
      }
    }
    if (handle) {
      var h = '-' + handle + '-';
      var slugs = schoolSlugs();
      for (i = 0; i < slugs.length; i++) {
        if (h.indexOf('-' + slugs[i] + '-') !== -1) return slugs[i];
      }
    }
    return null;
  }

  function realItems(cart) {
    return cart.items.filter(function (i) { return i.handle !== CP_HANDLE; });
  }

  function productSubtotalCents(cart) {
    return realItems(cart).reduce(function (sum, i) {
      return sum + (typeof i.final_line_price === 'number' ? i.final_line_price : i.line_price);
    }, 0);
  }

  function gapCents(cart) {
    return Math.max(0, THRESHOLD_CENTS - productSubtotalCents(cart));
  }

  /* Dominant school of the cart (by quantity) -> accent color. */
  function cartSchools(cart) {
    var counts = {};
    realItems(cart).forEach(function (item) {
      var slug = detectSchool(null, item.handle);
      if (slug) counts[slug] = (counts[slug] || 0) + item.quantity;
    });
    return counts;
  }

  /* The cart's school color, or null when the cart holds no school or
     more than one (Harrison, 2026-10-17: a mixed Auburn + Utah cart uses
     the common brand colors). Colors too light to carry white text are
     skipped so a gold-primary school never produces an unreadable button. */
  function cartSchoolAccent(cart) {
    var counts = cartSchools(cart);
    var slugs = Object.keys(counts);
    if (slugs.length !== 1) return null;
    var entry = schoolMap()[slugs[0]];
    var color = entry && entry.color;
    if (!color || !/^#[0-9a-f]{6}$/i.test(color)) return null;
    return relativeLuminance(color) > 0.45 ? null : color;
  }

  function relativeLuminance(hex) {
    var c = [1, 3, 5].map(function (i) {
      var v = parseInt(hex.substr(i, 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function dominantSchoolColor(cart) {
    return cartSchoolAccent(cart) || '#121212';
  }

  /* Expose the school color to the drawer snippets (Checkout button,
     Checkout+ switch, cart count, savings figure read --cart-accent and
     fall back to the brand colors when it is unset). The app owns the
     drawer root's style attribute, so re-apply on every render pass. */
  var NEUTRAL_ACCENT = '#121212';

  function setRootVar(root, name, value) {
    if (value) {
      if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
    } else if (root.style.getPropertyValue(name)) {
      root.style.removeProperty(name);
    }
  }

  function applyCartAccent() {
    var root = document.getElementById('slidecarthq');
    if (!root || !state.cart) return;
    var slugs = Object.keys(cartSchools(state.cart));
    var school = cartSchoolAccent(state.cart);
    /* One school: its color everywhere. Two or more: the neutral the bar
       uses (the brand orange read as Auburn next to a Utah hoodie), with
       the savings figure keeping its rust. No school product: brand. */
    var accent = school || (slugs.length > 1 ? NEUTRAL_ACCENT : null);
    setRootVar(root, '--cart-accent', accent);
    setRootVar(root, '--cart-school-accent', school);
    if (school) root.setAttribute('data-cart-school', slugs[0]);
    else if (slugs.length > 1) root.setAttribute('data-cart-school', 'mixed');
    else root.removeAttribute('data-cart-school');
  }

  /* ------------------------------------------------------------------ */
  /* Drive bar                                                           */
  /* ------------------------------------------------------------------ */

  /* Layout-critical styles are INLINE on every element this module
     builds. The drawer shares the page with the app bundle, the compiled
     section bundle, and critical.css - class-only layout rules proved
     overridable in that environment (stacked cards, collapsed flex).
     Inline styles win against any stylesheet without !important wars.
     Classes carry only cosmetics (fonts, colors, borders, hover). */

  var OVAL_SVG =
    '<svg class="drive-bar__oval" viewBox="0 0 200 44" preserveAspectRatio="none" aria-hidden="true" ' +
    'style="position:absolute;top:-4px;left:-8px;width:calc(100% + 16px);height:calc(100% + 8px);pointer-events:none;">' +
    '<path d="M100 3 C 155 1, 196 9, 197 22 C 198 36, 152 42, 98 41 C 46 42, 4 35, 3 22 C 2 10, 42 2, 100 3" ' +
    'fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>';

  /* The goal ("$100" + flag) is one unit pinned to the RIGHT end of the
     top row - the flag marks the number as the goal line. margin-left:auto
     keeps it right-pinned even if the flex row were ever interfered with. */
  var FLAG_SVG =
    '<svg class="drive-bar__flag" viewBox="0 0 12 16" aria-hidden="true" ' +
    'style="display:block;width:11px;height:15px;flex:0 0 auto;">' +
    '<path d="M2 15 V1.5 M2 2 L10 4.5 L2 7" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function buildBar() {
    var bar = document.createElement('div');
    bar.className = 'drive-bar';
    bar.style.cssText = 'display:block;padding:12px 18px 13px;';
    bar.innerHTML =
      '<div class="drive-bar__row" style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin:0 0 8px;">' +
        '<span class="drive-bar__label" data-drive-label style="display:block;min-width:0;"></span>' +
        '<span class="drive-bar__goal" data-drive-goal style="display:inline-flex;align-items:center;gap:5px;flex:0 0 auto;margin-left:auto;">' +
          '<span data-drive-goal-amount></span>' + FLAG_SVG +
        '</span>' +
      '</div>' +
      '<div class="drive-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="Progress toward free shipping" ' +
        'style="position:relative;display:block;height:12px;overflow:hidden;">' +
        '<div class="drive-bar__fill" data-drive-fill style="position:absolute;top:0;bottom:0;left:0;width:0;"></div>' +
      '</div>';
    return bar;
  }

  function barState(cart) {
    var subtotal = productSubtotalCents(cart);
    var gap = gapCents(cart);
    var pct = Math.max(0, Math.min(100, Math.round((subtotal / THRESHOLD_CENTS) * 100)));
    return {
      visible: realItems(cart).length > 0,
      unlocked: gap === 0,
      redZone: gap > 0 && gap <= RED_ZONE_CENTS,
      gap: gap,
      pct: pct
    };
  }

  function renderBarInto(bar, cart) {
    var st = barState(cart);
    if (!st.visible) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;

    var label = bar.querySelector('[data-drive-label]');
    var goal = bar.querySelector('[data-drive-goal]');
    var fill = bar.querySelector('[data-drive-fill]');
    var track = bar.querySelector('.drive-bar__track');

    var labelHtml;
    if (st.unlocked) {
      labelHtml = '<span class="drive-bar__touchdown" style="position:relative;display:inline-block;padding:3px 10px;">' +
        OVAL_SVG + '<span style="position:relative;">TOUCHDOWN \u2014 FREE SHIPPING UNLOCKED</span></span>';
    } else {
      labelHtml = formatMoneyShort(st.gap) + ' FROM FREE SHIPPING' +
        (st.redZone ? ' <span class="drive-bar__zone" style="display:inline-block;margin-left:6px;padding:1px 6px;vertical-align:1px;">RED ZONE</span>' : '');
    }
    /* Re-render the label only when its state changes. Comparing against
       label.innerHTML never matched in the unlocked state (the browser
       serializes the oval's <path/> as <path></path>), so the oval was
       re-created on every mutation tick and its intro animation restarted
       forever, leaving it invisible. */
    var labelKey = st.unlocked ? 'unlocked' : st.gap + ':' + (st.redZone ? 'red' : 'open');
    if (label.getAttribute('data-drive-state') !== labelKey) {
      label.innerHTML = labelHtml;
      label.setAttribute('data-drive-state', labelKey);
    }

    var amount = goal.querySelector('[data-drive-goal-amount]');
    var goalText = formatMoneyShort(THRESHOLD_CENTS);
    if (amount && amount.textContent !== goalText) amount.textContent = goalText;
    goal.style.display = st.unlocked ? 'none' : 'inline-flex';

    var color = dominantSchoolColor(cart);
    var width = st.pct + '%';
    if (fill.style.width !== width) fill.style.width = width;
    if (fill.style.backgroundColor !== color) fill.style.backgroundColor = color;
    track.setAttribute('aria-valuenow', st.pct);
    bar.classList.toggle('drive-bar--unlocked', st.unlocked);
  }

  function renderDrawerBar() {
    if (!cfg.barEnabled || !state.cart) return;
    var drawer = document.getElementById('slidecarthq');
    if (!drawer) return;
    var header = drawer.querySelector('.header');
    var bar = drawer.querySelector('.drive-bar');
    if (!header) {
      if (bar) bar.remove();
      return;
    }

    if (!bar) {
      bar = buildBar();
      var anchor = drawer.querySelector('.announcements') || header;
      anchor.parentNode.insertBefore(bar, anchor.nextSibling);
    }
    renderBarInto(bar, state.cart);
  }

  /* Cart page placeholder (server-rendered container, JS fills). */
  function renderPageTargets() {
    document.querySelectorAll('[data-drive-bar-page]').forEach(function (holder) {
      if (!cfg.barEnabled || !state.cart) return;
      var bar = holder.querySelector('.drive-bar');
      if (!bar) {
        bar = buildBar();
        holder.appendChild(bar);
      }
      renderBarInto(bar, state.cart);
    });
  }

  /* Express wallets in the drawer footer: layout/theme.liquid trims
     Shopify's wallet list to Shop Pay + Apple Pay and stacks them under
     Checkout (42px each via a CSS custom property in cart-drive.liquid).
     Insurance for the day that trim stops working: never show more than
     two, because a four-wallet stack buries the cart on a phone. Counted
     from the component's children, not measured, so it cannot misfire
     mid-render. */
  /* The app moves the layout's wallet markup into its footer once and
     destroys it with the footer when the cart empties; the next footer it
     builds has an empty wallet slot. Put a fresh copy back (the custom
     element upgrades and renders again on insertion). */
  function restoreWalletStack() {
    var markup = window.__saturdayWalletMarkup;
    if (!markup) return;
    var slot = document.querySelector('#slidecarthq .footer .additional-checkout-buttons');
    if (!slot || slot.querySelector('shopify-accelerated-checkout-cart')) return;
    if (document.querySelector('#slidecart-additional-checkout-buttons shopify-accelerated-checkout-cart')) return;
    slot.insertAdjacentHTML('beforeend', markup);
  }

  function capWalletStack() {
    var comp = document.querySelector('#slidecarthq .additional-checkout-buttons shopify-accelerated-checkout-cart');
    if (!comp) return;
    var block = comp.closest('.additional-checkout-buttons');
    if (!block) return;
    if (comp.children.length > 2 && block.style.display !== 'none') {
      block.style.setProperty('display', 'none', 'important');
    }
  }

  /* Preorder mode: one quiet line at the bottom of the drawer footer so
     shoppers reaching checkout are not surprised by the ship date. */
  function renderDrawerPreorderNote() {
    if (!cfg.preorderNote) return;
    var drawer = document.getElementById('slidecarthq');
    if (!drawer) return;
    var footer = drawer.querySelector('.footer');
    var note = drawer.querySelector('.cart-preorder-note');
    if (!footer) {
      if (note) note.remove();
      return;
    }
    if (!note) {
      note = document.createElement('p');
      note.className = 'cart-preorder-note';
      note.style.margin = '8px 0 0';
      note.textContent = cfg.preorderNote;
      footer.appendChild(note);
    }
  }

  var REMINDER_KEY = 'saturday:cart-reminder:v1';
  var REMINDER_DURATION = 10 * 60 * 1000;
  var reminderDeadline = null;
  var reminderStorageLoaded = false;
  var reminderStorageAvailable = true;
  var reminderTimeout = null;
  var reminderRows = [];
  var reminderObserver = cfg.stockAlert && cfg.stockAlert.timerEnabled && window.IntersectionObserver
    ? new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target._reminderVisible = entry.isIntersecting;
      });
      updateReminderClock();
    }) : null;

  function loadReminderDeadline() {
    if (reminderStorageLoaded) return;
    reminderStorageLoaded = true;
    try {
      var saved = Number(window.sessionStorage.getItem(REMINDER_KEY));
      if (Number.isFinite(saved) && saved > 0) reminderDeadline = saved;
    } catch (e) {
      reminderStorageAvailable = false;
    }
  }

  function resetReminderClock() {
    clearTimeout(reminderTimeout);
    reminderDeadline = null;
    if (!reminderObserver) return;
    reminderStorageLoaded = true;
    try {
      window.sessionStorage.removeItem(REMINDER_KEY);
    } catch (e) {
      reminderStorageAvailable = false;
    }
  }

  function updateReminderClock() {
    clearTimeout(reminderTimeout);
    if (!reminderObserver || !reminderStorageAvailable || document.hidden) return;
    if (!state.cart || !realItems(state.cart).length) return;
    var visible = reminderRows.some(function (row) {
      return row.isConnected && !row.hidden && row._reminderVisible;
    });
    if (!visible) return;

    if (reminderDeadline === null) {
      reminderDeadline = Date.now() + REMINDER_DURATION;
      try {
        window.sessionStorage.setItem(REMINDER_KEY, String(reminderDeadline));
      } catch (e) {
        reminderStorageAvailable = false;
        reminderRows.forEach(function (row) { row._reminderTimer.hidden = true; });
        return;
      }
    }

    var seconds = Math.max(0, Math.ceil((reminderDeadline - Date.now()) / 1000));
    var text = seconds > 0
      ? String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0')
      : cfg.stockAlert.completeText || '✓';
    reminderRows.forEach(function (row) {
      var timer = row._reminderTimer;
      if (timer.firstChild.nodeValue !== text) timer.firstChild.nodeValue = text;
      if (!seconds && !timer.hasAttribute('data-complete')) {
        timer.setAttribute('data-complete', '');
        timer.setAttribute('aria-label', cfg.stockAlert.completeDescription);
        timer.title = cfg.stockAlert.completeDescription;
      } else if (seconds && timer.hasAttribute('data-complete')) {
        timer.removeAttribute('data-complete');
        timer.setAttribute('aria-label', cfg.stockAlert.timerLabel);
        timer.title = cfg.stockAlert.timerDescription;
      }
    });
    if (seconds > 0) reminderTimeout = setTimeout(updateReminderClock, 1000);
  }

  function enhanceReminderRows() {
    if (!reminderObserver || !state.cart) return;
    reminderRows = reminderRows.filter(function (row) {
      if (row.isConnected && !row.hidden) return true;
      reminderObserver.unobserve(row);
      row._reminderObserved = false;
      return false;
    });
    loadReminderDeadline();
    if (!reminderStorageAvailable) return;
    document.querySelectorAll('[data-cart-reminder]').forEach(function (row) {
      if (row.hidden || row._reminderObserved) return;
      var timer = row.querySelector('[data-cart-reminder-timer]');
      if (!timer) {
        timer = document.createElement('span');
        timer.className = 'cart-reminder__timer';
        timer.setAttribute('data-cart-reminder-timer', '');
        timer.setAttribute('role', 'timer');
        timer.setAttribute('aria-live', 'off');
        timer.setAttribute('aria-label', cfg.stockAlert.timerLabel);
        timer.title = cfg.stockAlert.timerDescription;
        timer.style.cssText = 'position:relative;display:inline-flex;align-items:center;justify-content:center;flex:0 0 var(--cart-reminder-timer-width,52px);height:21px;';
        timer.appendChild(document.createTextNode(String(REMINDER_DURATION / 60000).padStart(2, '0') + ':00'));
        var timerLabel = document.createElement('span');
        timerLabel.className = 'cart-reminder__timer-label';
        timerLabel.setAttribute('aria-hidden', 'true');
        timerLabel.textContent = cfg.stockAlert.timerLabel;
        timer.appendChild(timerLabel);
        row.appendChild(timer);
      }
      row._reminderTimer = timer;
      row._reminderVisible = false;
      row._reminderObserved = true;
      reminderRows.push(row);
      reminderObserver.observe(row);
    });
    updateReminderClock();
  }

  function renderCartReminder() {
    var show = !!(cfg.stockAlert && cfg.stockAlert.text && state.cart && realItems(state.cart).length);
    document.querySelectorAll('cart-page [data-cart-reminder]').forEach(function (row) {
      if (state.cart) row.hidden = !show;
    });
    var drawer = document.getElementById('slidecarthq');
    var footer = drawer && drawer.querySelector('.footer');
    var el = drawer && drawer.querySelector('.cart-stock-alert');
    if (!footer || !show) {
      if (el) el.remove();
      enhanceReminderRows();
      return;
    }
    if (!el) {
      el = document.createElement('p');
      el.className = 'cart-stock-alert cart-reminder';
      el.setAttribute('data-cart-reminder', '');
      el.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:var(--cart-reminder-gap,10px);margin:0 0 10px;';
      var copy = document.createElement('span');
      copy.className = 'cart-reminder__copy';
      copy.style.minWidth = '0';
      if (cfg.stockAlert.label) {
        var label = document.createElement('strong');
        label.textContent = cfg.stockAlert.label;
        copy.appendChild(label);
        copy.appendChild(document.createTextNode(' '));
      }
      var message = document.createElement('span');
      message.className = 'cart-reminder__message';
      message.textContent = cfg.stockAlert.text;
      copy.appendChild(message);
      el.appendChild(copy);
    }
    if (el.parentNode !== footer || footer.firstChild !== el) {
      footer.insertBefore(el, footer.firstChild);
    }
    enhanceReminderRows();
  }

  function renderAllModules() {
    applyCartAccent();
    renderDrawerBar();
    renderPageTargets();
    restoreWalletStack();
    capWalletStack();
    renderDrawerPreorderNote();
    renderCartReminder();
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function onCartData(cart) {
    if (!cart || !cart.items) return;
    state.cart = cart;
    if (!realItems(cart).length) resetReminderClock();
    renderAllModules();
  }

  var syncTimer = null;
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      fetchCart().then(function (cart) { if (cart) onCartData(cart); });
    }, 200);
  }

  var prevUpdated = window.SLIDECART_UPDATED;
  window.SLIDECART_UPDATED = function (cart) {
    if (typeof prevUpdated === 'function') {
      try { prevUpdated(cart); } catch (e) { /* third-party hook error */ }
    }
    if (cart && cart.items) onCartData(cart);
  };

  document.addEventListener('checkout-plus:cart', function (e) {
    if (e.detail && e.detail.cart) onCartData(e.detail.cart);
  });

  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)(\.js|\b)/.test(url)) {
      result.then(function (res) {
        if (res && res.ok) scheduleSync();
      }).catch(function () { /* network error */ });
    }
    return result;
  };

  function observeDrawer() {
    var root = document.getElementById('slidecarthq');
    if (!root) {
      setTimeout(observeDrawer, 500);
      return;
    }
    renderAllModules();
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        applyCartAccent();
        renderDrawerBar();
        restoreWalletStack();
        capWalletStack();
        renderDrawerPreorderNote();
        renderCartReminder();
      });
    }).observe(root, { childList: true, subtree: true });
  }

  function init() {
    observeDrawer();

    /* Cart page: the section replaces its own innerHTML after quantity
       changes, wiping the JS-filled placeholder - re-fill on swap. */
    var cartPage = document.querySelector('cart-page');
    var cartSection = cartPage && cartPage.closest('.shopify-section');
    if (cartSection) {
      new MutationObserver(function () {
        requestAnimationFrame(function () {
          renderPageTargets();
          renderCartReminder();
        });
      }).observe(cartSection, { childList: true });
    }

    fetchCart().then(function (cart) { if (cart) onCartData(cart); });
    if (reminderObserver) document.addEventListener('visibilitychange', updateReminderClock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CartDrive = {
    refresh: scheduleSync,
    getState: function () { return state; },
    _test: {
      thresholdCents: THRESHOLD_CENTS,
      barState: barState,
      detectSchool: detectSchool,
      cartSchoolAccent: cartSchoolAccent
    }
  };
})();
